import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi, saveAuthSession } from '../services/api';
import '../styles/AuthPages.css';

function ClientRegistrationForm() {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const validateForm = () => {
    const errors = {};

    if (!name.trim()) {
      errors.name = 'Full name is required';
    } else if (name.trim().length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }

    if (!email.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!agreeTerms) {
      errors.agreeTerms = 'Please agree to the Terms & Privacy Policy';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = await authApi.register({
        name: name.trim(),
        email: email.trim(),
        password,
        role: 'client',
      });

      saveAuthSession({ token: payload.token, user: payload.user });
      setStatusMessage('Account created successfully! Redirecting you to sign in...');

      window.setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (error) {
      setErrorMessage(error.message || 'Unable to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="hb-auth-page">
      <div className="hb-split-card">
        {/* Left Side: Soothing Wellness Visual & Message */}
        <div className="hb-visual-panel">
          <img
            src="/assets/images/auth-soothing-wellness.jpg"
            alt="Find your calm - PsychMitra Wellness"
            className="hb-visual-bg-img"
          />
          <div className="hb-visual-overlay"></div>

          <div className="hb-visual-content-top">
            <div className="hb-calm-badge">
              <span className="hb-pulse-dot"></span>
              PSYCHMITRA WELLBEING
            </div>
          </div>

          <div className="hb-visual-content-bottom">
            <div className="hb-quote-box">
              <p className="hb-quote-text">
                "Finding support was the kindest choice I made for myself. PsychMitra made talking to a verified psychologist feel safe, natural, and genuinely comforting."
              </p>
              <p className="hb-quote-author">— Priya S., Verified Client</p>
            </div>

            <div className="hb-pill-chips-row">
              <span className="hb-pill-chip">
                <i className="fas fa-lock"></i> 100% Confidential
              </span>
              <span className="hb-pill-chip">
                <i className="fas fa-certificate"></i> Verified Specialists
              </span>
              <span className="hb-pill-chip">
                <i className="fas fa-shield-heart"></i> Evidence-Based
              </span>
            </div>
          </div>
        </div>

        {/* Right Side: Minimalist Registration Form */}
        <div className="hb-form-panel">
          {/* Role Switcher Tabs */}
          <div className="hb-role-toggle-bar">
            <button type="button" className="hb-role-tab-btn active">
              <i className="fas fa-heart"></i> For Individuals
            </button>
            <Link to="/pricing" className="hb-role-tab-btn">
              <i className="fas fa-user-doctor"></i> For Practitioners
            </Link>
          </div>

          <div className="hb-form-header">
            <h1 className="hb-form-title">Begin Your Journey</h1>
            <p className="hb-form-subtitle">
              Create your private account to connect with certified psychologists and therapists.
            </p>
          </div>

          {/* Feedback Alerts */}
          {errorMessage && (
            <div className="hb-alert hb-alert-error">
              <i className="fas fa-circle-exclamation"></i>
              <span>{errorMessage}</span>
            </div>
          )}

          {statusMessage && (
            <div className="hb-alert hb-alert-success">
              <i className="fas fa-circle-check"></i>
              <span>{statusMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Full Name */}
            <div className="hb-form-group">
              <label className="hb-label" htmlFor="fullName">Full Name</label>
              <div className="hb-input-wrap">
                <i className="fas fa-user hb-input-icon"></i>
                <input
                  id="fullName"
                  type="text"
                  className={`hb-input ${validationErrors.name ? 'has-error' : ''}`}
                  placeholder="e.g. Sarah Jenkins"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (validationErrors.name) setValidationErrors({ ...validationErrors, name: '' });
                  }}
                  required
                />
              </div>
              {validationErrors.name && (
                <span className="hb-error-msg">{validationErrors.name}</span>
              )}
            </div>

            {/* Email */}
            <div className="hb-form-group">
              <label className="hb-label" htmlFor="emailAddress">Email Address</label>
              <div className="hb-input-wrap">
                <i className="fas fa-envelope hb-input-icon"></i>
                <input
                  id="emailAddress"
                  type="email"
                  className={`hb-input ${validationErrors.email ? 'has-error' : ''}`}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (validationErrors.email) setValidationErrors({ ...validationErrors, email: '' });
                  }}
                  required
                  autoComplete="email"
                />
              </div>
              {validationErrors.email && (
                <span className="hb-error-msg">{validationErrors.email}</span>
              )}
            </div>

            {/* Password */}
            <div className="hb-form-group">
              <label className="hb-label" htmlFor="password">Create Password</label>
              <div className="hb-input-wrap">
                <i className="fas fa-lock hb-input-icon"></i>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`hb-input ${validationErrors.password ? 'has-error' : ''}`}
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (validationErrors.password) setValidationErrors({ ...validationErrors, password: '' });
                  }}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="hb-pw-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {validationErrors.password && (
                <span className="hb-error-msg">{validationErrors.password}</span>
              )}
            </div>

            {/* Confirm Password */}
            <div className="hb-form-group">
              <label className="hb-label" htmlFor="confirmPassword">Confirm Password</label>
              <div className="hb-input-wrap">
                <i className="fas fa-shield-alt hb-input-icon"></i>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`hb-input ${validationErrors.confirmPassword ? 'has-error' : ''}`}
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (validationErrors.confirmPassword) setValidationErrors({ ...validationErrors, confirmPassword: '' });
                  }}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="hb-pw-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  title={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {validationErrors.confirmPassword && (
                <span className="hb-error-msg">{validationErrors.confirmPassword}</span>
              )}
            </div>

            {/* Terms Checkbox */}
            <div className="hb-terms-row">
              <input
                type="checkbox"
                id="agreeTerms"
                className="hb-checkbox"
                checked={agreeTerms}
                onChange={(e) => {
                  setAgreeTerms(e.target.checked);
                  if (validationErrors.agreeTerms) setValidationErrors({ ...validationErrors, agreeTerms: '' });
                }}
                required
              />
              <label htmlFor="agreeTerms" style={{ cursor: 'pointer' }}>
                I agree to the <Link to="/terms" className="hb-link" target="_blank">Terms of Service</Link> and <Link to="/privacy" className="hb-link" target="_blank">Privacy Policy</Link>.
              </label>
            </div>
            {validationErrors.agreeTerms && (
              <span className="hb-error-msg" style={{ marginTop: '-16px', marginBottom: '16px' }}>
                {validationErrors.agreeTerms}
              </span>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="hb-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Creating Account...</span>
                </>
              ) : (
                <>
                  <span>Create Free Account</span>
                  <i className="fas fa-arrow-right"></i>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="hb-form-footer">
            <p style={{ margin: 0 }}>
              Already have an account? <Link to="/login">Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientRegistrationForm;
